import { v4 as uuidv4 } from "uuid";
import { useCallback, useEffect } from "react";

import {
  actionNames,
  actionPrefixes,
  eventTypes,
  objectTypes,
  useTracker,
} from "@newsela/snitch";
import {
  HOMEPAGE_SESSION_KEY,
  SEARCH_SESSION_KEY,
} from "@/base/NewselaGlobalLayoutV2/NavigationBar/shared/constants";

/**
 * A custom hook for initializing a new Snitch homepage session tracker.
 *
 * Called on mount of TeacherHomepage. This hook:
 * 1. Generates a new unique ID for the homepage session.
 * 2. Persists the ID to `window.sessionStorage`.
 * 3. Updates Snitch's homepage session ID (auto-attached to all subsequent events).
 * 4. Fires a Snitch event indicating a homepage session has begun.
 *
 * A new session ID is generated on each homepage visit, so each discrete
 * visit is independently attributable in BigQuery.
 */
export function useHomepageSessionTracker() {
  const { trackAction, setHomepageSessionId } = useTracker();

  useEffect(() => {
    const newHomepageSessionId = uuidv4();
    window.sessionStorage.setItem(HOMEPAGE_SESSION_KEY, newHomepageSessionId);
    setHomepageSessionId(newHomepageSessionId);

    trackAction({
      event_name: "Homepage Initiate Session",
      event_type: eventTypes.nativeInteraction,
      dimension_properties: {
        description: "Homepage session started",
        homepage_session_id: newHomepageSessionId,
      },
    });
  }, []);
}

/**
 * A custom hook for initializing a new Snitch search tracking session.
 *
 * When the returned function `startSearchTracker` is called, this
 * hook performs four main actions:
 * 1. Generates a new unique ID for the search session.
 * 2. Persists the ID to `window.sessionStorage`.
 * 3. Updates Snitch's search session ID.
 * 4. Fires a Snitch event indicating a search has been initiated.
 *
 * @param metadata - Optional metadata attached to the Snitch event.
 * @returns Object containing the `startSearchTracker` function.
 */
export function useNewSearchSessionTracker(metadata?: Record<string, unknown>) {
  const { trackAction, getEventName, setSearchSessionId } = useTracker();
  const startSearchTracker = useCallback(
    (searchInput: string) => {
      const newSearchSessionId = uuidv4();
      window.sessionStorage.setItem(SEARCH_SESSION_KEY, newSearchSessionId);
      setSearchSessionId(newSearchSessionId);

      trackAction({
        event_name: getEventName(
          objectTypes.content,
          actionPrefixes.initiate,
          actionNames.search,
        ),
        event_type: eventTypes.nativeInteraction,
        dimension_properties: {
          description: "Search Session Started",
          search_session_id: newSearchSessionId,
          search_term: searchInput,
          ...(metadata ? { metadata } : null),
        },
      });
    },
    [trackAction, getEventName, setSearchSessionId],
  );
  return { startSearchTracker };
}
